/**
 * UserStickies Component
 *
 * Container orchestrator for user-created sticky notes.
 * Appears on ALL /app pages via Portal rendering.
 *
 * Features:
 * - Portal rendering to escape layout constraints
 * - Expanded panel management
 * - Minimized pill dock with create button
 * - PostgreSQL persistence via useUserStickies hook
 * - Delete confirmation modal
 * - Mobile: Full-screen single-note view with navigation
 */

import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import { Portal } from '@shared/components/ui/Portal';
import { UserStickyPanel } from './UserStickyPanel';
import { MobileFullScreenSticky } from './MobileFullScreenSticky';
import { UserStickyPillDock } from './UserStickyPillDock';
import { DeleteConfirmModal } from './DeleteConfirmModal';
import { useUserStickies } from '@shared/hooks/useUserStickies';
import { useIsMobile } from '@shared/hooks/useIsMobile';
import type { StickyPosition, StickySize } from '@shared/types/userStickies';

/**
 * Main container for User Stickies feature
 * Renders on all /app pages
 */
export const UserStickies: React.FC = () => {
  const {
    stickies,
    isLoading,
    deleteModalOpen,
    isDockCollapsed,
    dockDisplayMode,
    addSticky,
    setContent,
    setTitle,
    setPosition,
    setSize,
    toggleCollapsed,
    setCollapsed,
    toggleDockCollapsed,
    setDockDisplayMode,
    cycleDockDisplayMode,
    openDeleteModal,
    closeDeleteModal,
    confirmDelete,
  } = useUserStickies();

  const isMobile = useIsMobile();

  // Track which sticky is focused/selected (for z-index management on desktop)
  const [focusedStickyId, setFocusedStickyId] = useState<string | null>(null);

  // Mobile: track which expanded sticky index is currently shown (0-based)
  const [mobileActiveIndex, setMobileActiveIndex] = useState(0);

  // Get expanded (not collapsed) stickies
  const expandedStickies = useMemo(
    () => stickies.filter((s) => !s.isCollapsed),
    [stickies]
  );

  // Get collapsed stickies for pill dock
  const collapsedStickies = useMemo(
    () => stickies.filter((s) => s.isCollapsed),
    [stickies]
  );

  // Keep mobile index in bounds when stickies change
  useEffect(() => {
    if (mobileActiveIndex >= expandedStickies.length && expandedStickies.length > 0) {
      setMobileActiveIndex(expandedStickies.length - 1);
    }
  }, [expandedStickies.length, mobileActiveIndex]);

  // Handlers for individual sticky panels
  const handleContentChange = useCallback(
    (id: string) => (content: string) => setContent(id, content),
    [setContent]
  );

  const handleTitleChange = useCallback(
    (id: string) => (title: string) => setTitle(id, title),
    [setTitle]
  );

  const handlePositionChange = useCallback(
    (id: string) => (position: StickyPosition) => setPosition(id, position),
    [setPosition]
  );

  const handleSizeChange = useCallback(
    (id: string) => (size: StickySize) => setSize(id, size),
    [setSize]
  );

  const handleMinimize = useCallback(
    (id: string) => () => toggleCollapsed(id),
    [toggleCollapsed]
  );

  const handleDelete = useCallback(
    (id: string) => () => openDeleteModal(id),
    [openDeleteModal]
  );

  // Handler for bringing sticky to front (z-index management)
  const handleFocus = useCallback(
    (id: string) => () => setFocusedStickyId(id),
    []
  );

  // Handler for restoring collapsed stickies
  const handleRestore = useCallback(
    (id: string) => {
      setCollapsed(id, false);
      // On mobile, navigate to the restored sticky
      if (isMobile) {
        const restoredIndex = stickies.findIndex((s) => s.id === id);
        const newExpandedIndex = stickies
          .filter((s) => !s.isCollapsed || s.id === id)
          .findIndex((s) => s.id === id);
        if (newExpandedIndex >= 0) {
          setMobileActiveIndex(newExpandedIndex);
        }
      }
    },
    [setCollapsed, isMobile, stickies]
  );

  // Handler for creating new sticky
  const handleCreateNew = useCallback(() => {
    const newId = addSticky();
    // On mobile, navigate to the new sticky (will be at the end)
    if (isMobile) {
      // New sticky is added to end of expanded list
      setMobileActiveIndex(expandedStickies.length);
    }
  }, [addSticky, isMobile, expandedStickies.length]);

  // Mobile navigation handlers
  const handleMobileNext = useCallback(() => {
    setMobileActiveIndex((prev) => Math.min(prev + 1, expandedStickies.length - 1));
  }, [expandedStickies.length]);

  const handleMobilePrev = useCallback(() => {
    setMobileActiveIndex((prev) => Math.max(prev - 1, 0));
  }, []);

  // Don't render while loading
  if (isLoading) {
    return null;
  }

  // Mobile: Show full-screen single sticky view
  if (isMobile && expandedStickies.length > 0) {
    const activeSticky = expandedStickies[mobileActiveIndex];
    if (!activeSticky) return null;

    return (
      <Portal>
        <MobileFullScreenSticky
          sticky={activeSticky}
          currentIndex={mobileActiveIndex}
          totalCount={expandedStickies.length}
          onContentChange={handleContentChange(activeSticky.id)}
          onTitleChange={handleTitleChange(activeSticky.id)}
          onMinimize={handleMinimize(activeSticky.id)}
          onDelete={handleDelete(activeSticky.id)}
          onNext={handleMobileNext}
          onPrev={handleMobilePrev}
        />

        {/* Delete Confirmation Modal */}
        <DeleteConfirmModal
          isOpen={deleteModalOpen}
          onCancel={closeDeleteModal}
          onConfirm={confirmDelete}
        />
      </Portal>
    );
  }

  // Mobile with no expanded stickies: just show dock
  if (isMobile) {
    return (
      <Portal>
        <UserStickyPillDock
          collapsedStickies={collapsedStickies}
          onRestore={handleRestore}
          onCreateNew={handleCreateNew}
          dockDisplayMode={dockDisplayMode}
          onCycleDockDisplayMode={cycleDockDisplayMode}
          onSetDockDisplayMode={setDockDisplayMode}
        />

        <DeleteConfirmModal
          isOpen={deleteModalOpen}
          onCancel={closeDeleteModal}
          onConfirm={confirmDelete}
        />
      </Portal>
    );
  }

  // Desktop: Show all expanded panels
  return (
    <Portal>
      {/* Expanded Panels */}
      <AnimatePresence mode="popLayout">
        {expandedStickies.map((sticky) => (
          <UserStickyPanel
            key={sticky.id}
            sticky={sticky}
            isFocused={focusedStickyId === sticky.id}
            onFocus={handleFocus(sticky.id)}
            onContentChange={handleContentChange(sticky.id)}
            onTitleChange={handleTitleChange(sticky.id)}
            onPositionChange={handlePositionChange(sticky.id)}
            onSizeChange={handleSizeChange(sticky.id)}
            onMinimize={handleMinimize(sticky.id)}
            onDelete={handleDelete(sticky.id)}
          />
        ))}
      </AnimatePresence>

      {/* Minimized Pill Dock with Menu Button */}
      <UserStickyPillDock
        collapsedStickies={collapsedStickies}
        onRestore={handleRestore}
        onCreateNew={handleCreateNew}
        dockDisplayMode={dockDisplayMode}
        onCycleDockDisplayMode={cycleDockDisplayMode}
        onSetDockDisplayMode={setDockDisplayMode}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={deleteModalOpen}
        onCancel={closeDeleteModal}
        onConfirm={confirmDelete}
      />
    </Portal>
  );
};
